import { i, s, x } from '../../lit-element.js';
import { reset } from '../../styles/reset.style.js';
import { links } from '../../styles/links.style.js';
import { buttons } from '../../styles/buttons.style.js';
import { RefiAdobeTarget } from '../../adobe-target/adobe-target.js';
import '../../utils/load-script.js';
import '../../utils/wait-for-dom.js';
import '../../utils/refi-mfe-uuid.js';
import '../../utils/track-analytics-event.js';

const style = i`
  :host {
    background-color: #f4f4f4;
    box-shadow: 0 2px 2px #e2e2e2 inset;
    display: block;
  }

  :host([hidden]) {
    display: none;
  }

  footer {
    margin: 0 auto;
  }

  .need-help {
    margin: 0 1.5em;
    padding: 1.5em 0;
    text-align: center;
  }

  h3 {
    font-size: 1.5em;
    font-weight: 300;
    margin-bottom: 1em;
  }

  h4,
  h5 {
    font-weight: 400;
    letter-spacing: 0.5px;
    line-height: 1.125;
  }

  .contact-hours {
    font-size: 0.875em;
    font-weight: 700;
    padding: 2rem 0;
    text-transform: uppercase;
  }

  @media (min-width: 60em) {
    footer {
      max-width: 60em;
    }

    h3 {
      font-size: 2em;
    }

    h4,
    h5 {
      font-size: 1.125em;
    }
  }

  .contact-options {
    display: grid;
    grid-template-columns: auto;
    justify-content: center;
    list-style-type: none;
    margin: 0 2em;
    text-align: left;
  }

  .contact-options li {
    display: grid;
    grid-template-areas:
      'icon link'
      '. content'
      '. footer';
    grid-template-columns: 2em auto;
    grid-template-rows: max-content auto;
    max-width: 18em;
  }

  .contact-options li:not(:last-of-type) {
    padding-bottom: 2em;
  }

  li svg {
    font-size: 1.5em;
    grid-area: icon;
  }

  li h5 {
    grid-area: link;
    margin-bottom: 0.25em;
  }

  li p {
    font-size: 0.875em;
    grid-area: content;
  }

  @media (min-width: 48em) {
    .contact-options {
      grid-gap: 0.5em;
      grid-template-columns: auto auto auto;
    }
  }

  refi-common-disclosures {
    margin-bottom: 0.75em;
  }

  nav {
    font-size: 0.75em;
    padding: 0 2em;
  }

  nav a {
    border-bottom: 1px solid #d4d4d4;
    display: block;
    padding: 0.75em 0;
  }

  @media (min-width: 48em) {
    nav a {
      border-bottom: none;
      display: inline;
      padding: 0 0.75em 0 0;
    }
  }

  .logos {
    display: flex;
    flex-direction: column;
    font-size: 0.75em;
    justify-content: flex-start;
    padding: 1.125em 2em 0;
  }

  .copyright {
    margin-bottom: 2em;
  }

  @media (min-width: 48em) {
    .logos {
      align-items: center;
      flex-direction: row;
      justify-content: space-between;
    }
  }

  .contact-options li.hidden {
    display: none;
  }

  li refi-common-tooltip {
    font-size: 14px;
    grid-area: footer;
    margin-top: 8px;
  }

  li refi-common-tooltip p {
    font-size: 16px;
    margin-bottom: 24px;
  }

  li refi-common-tooltip h2 {
    font-size: 32px;
    font-weight: 300;
    margin-bottom: 16px;
  }

  li refi-common-tooltip button {
    float: right;
    margin-bottom: 12px;
  }
`;

class RefiFooter extends s {
  constructor() {
    super();
    this.helpNumber = '1.888.263.4582';
    this.textNumber = '1.214.380.2131';
    this.showTextingContactOption = false;
  }

  static get properties() {
    return {
      canHelp: { type: Boolean, attribute: 'can-help' },
      helpNumber: { type: String, attribute: 'help-number' },
      showTextingContactOption: { type: Boolean, attribute: 'show-texting-contact-option' },
    };
  }

  static get styles() {
    return [reset, links, buttons, style];
  }

  connectedCallback() {
    super.connectedCallback();
    this.fetchShowTextingContactOption();
  }

  async fetchShowTextingContactOption() {
    try {
      await RefiAdobeTarget.waitForAdobeTargetToLoad();
      const offerResponse = await RefiAdobeTarget.getOffer({
        mbox: 'refi.texting-contact-option',
        params: {
          env: window.location.hostname === 'autorefi.capitalone.com' ? 'prod' : 'qa',
        },
      });
      this.showTextingContactOption = offerResponse?.offer?.[0]?.content?.[0]?.showTextingContactOption === true;
    } catch {
      this.showTextingContactOption = false;
    }
  }

  closeTextingInfoModal(ev) {
    const tooltip = ev.composedPath().find(elem => elem.nodeName.toLowerCase() === 'refi-common-tooltip');
    tooltip.closeModal();
  }

  render() {
    return x`
      <footer>
        ${this.canHelp
          ? x`
              <div class="need-help">
                <h3>We're here to help.</h3>
                <h4>Our team is ready to answer any questions you have about Auto Refinance.</h4>
                <p class="contact-hours" id="footerOperationHours"><refi-common-contact-hours></refi-common-contact-hours></p>
                <ul class="contact-options">
                  <li id="contactOptionPhone">
                    <svg width="19px" height="19px" viewBox="0 0 19 19" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
                      <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                        <g transform="translate(-879.000000, -400.000000)" fill="#687680">
                          <path
                            d="M887.111035,414.246044 C888.298835,415.435508 889.360203,416.273956 890.291811,416.76305 C891.5761,417.435139 893.550776,417.791146 894.843383,417.188928 C895.277579,416.984307 895.868152,416.553438 896.613438,415.896322 L894.004935,413.314436 C891.998651,415.320721 890.684418,414.683567 888.68146,412.675619 L885.593845,409.588004 C883.534325,407.531811 882.987006,406.325712 884.981645,404.29281 L882.373142,401.657689 C881.015656,403.321275 879.999205,405.137911 881.561312,408.044196 C882.067043,408.984122 882.88719,410.023863 884.02342,411.158429 L887.111035,414.246044 Z M883.224898,411.95695 C882.000499,410.732551 881.103826,409.578022 880.536543,408.496691 C878.538577,404.690407 880.318614,402.334769 881.78756,400.671183 C882.141904,400.315176 882.602717,400.316839 882.958725,400.671183 L885.993105,403.705564 C886.395693,404.151405 886.304196,404.510739 885.993105,404.851774 C884.720462,406.220906 884.292921,406.693364 886.392366,408.789482 L889.479982,411.877098 C891.556137,413.949926 892.04024,413.575619 893.44597,412.274695 C893.86353,411.935323 894.259464,411.976913 894.590518,412.302976 L897.598281,415.310739 C897.990887,415.743272 897.917689,416.225712 897.598281,416.483567 C896.674991,417.317024 895.929704,417.875989 895.362421,418.160462 C893.800314,418.935693 891.414732,418.566377 889.826007,417.759538 C888.743013,417.210555 887.571848,416.3039 886.312514,415.042902 L883.224898,411.95695 Z"
                            id="Phone"
                          ></path>
                        </g>
                      </g>
                    </svg>
                    <h5><a class="grv-link" href="tel:${this.helpNumber.replace(/\./g, '-')}" data-e2e-ref="footerContactNumberLink">Call ${this.helpNumber}</a></h5>
                    <p>Speak with one of our customer support specialists over the phone.</p>
                  </li>
                  <li class="${this.showTextingContactOption ? '' : 'hidden'}">
                    <svg width="19px" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <path
                        fill="#141414"
                        fill-rule="evenodd"
                        d="M19 19.5a2.5 2.5 0 0 1-2.336 2.495L16.5 22h-9a2.5 2.5 0 0 1-2.495-2.336L5 19.5v-15a2.5 2.5 0 0 1 2.336-2.495L7.5 2h9a2.5 2.5 0 0 1 2.495 2.336L19 4.5v15Zm-1-.5H6v.5a1.5 1.5 0 0 0 1.356 1.493L7.5 21h9a1.5 1.5 0 0 0 1.493-1.355L18 19.5V19ZM16.5 3h-9a1.5 1.5 0 0 0-1.493 1.356L6 4.5V18h12V4.5a1.5 1.5 0 0 0-1.355-1.493L16.5 3Zm-3 1a.5.5 0 0 1 0 1h-3a.5.5 0 0 1 0-1h3Z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    <h5><a class="grv-link" href="sms:${this.textNumber.replace(/\./g, '-')}" data-track="contact texting">Text ${this.textNumber}</a></h5>
                    <p>Chat with one of our customer support specialists over text.</p>
                    <refi-common-tooltip link-text="More about texting with Capital One" name="textingInfo">
                      <h2>About texting with Capital One.</h2>
                      <p>
                        By texting Capital One Auto Refinance, you consent to receive text messages relating to your inquiry and your relationship with Capital One Auto Refinance. You understand these
                        text messages may relate to the handling, servicing, and processing of your inquiries, or subsequent applications, for auto refinancing with Capital One Auto Refinance. Message
                        and data rates may apply.
                      </p>
                      <button class="grv-button action" type="button" @click="${this.closeTextingInfoModal}">Sounds good</button>
                    </refi-common-tooltip>
                  </li>
                  <li>
                    <svg width="19px" height="19px" viewBox="0 0 21 21" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
                      <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                        <g transform="translate(-399.000000, -479.000000)" fill="#263A48">
                          <path
                            d="M408.417671,493.673623 C408.417671,493.046451 408.930055,492.608928 409.482366,492.608928 C410.089575,492.608928 410.547061,493.066414 410.547061,493.673623 C410.547061,494.230924 410.102884,494.711701 409.482366,494.711701 C408.861848,494.711701 408.417671,494.230924 408.417671,493.673623 L408.417671,493.673623 Z M408.923401,491.57085 C408.923401,490.211701 409.246137,489.321682 410.201035,488.403383 C410.859815,487.791183 411.398817,487.31207 411.398817,486.460314 C411.398817,485.122791 409.081442,484.256063 406.847246,485.954584 L406.847246,484.756802 C408.931719,483.18305 412.756303,483.590628 412.756303,486.433697 C412.756303,487.534991 412.097523,488.270296 411.239113,488.988965 C410.380702,489.742569 410.07793,490.374732 410.041331,491.57085 L408.923401,491.57085 Z M400.751867,489.494695 C400.751867,494.319094 404.556488,498.411516 409.64207,498.411516 C414.55464,498.411516 418.532274,494.493771 418.532274,489.494695 C418.532274,484.515582 414.566285,480.551257 409.64207,480.551257 C404.772754,480.551257 400.751867,484.478983 400.751867,489.494695 L400.751867,489.494695 Z M399.580702,489.494695 C399.580702,484.008189 404.02414,479.433327 409.64207,479.433327 C415.096969,479.433327 419.703438,483.846821 419.703438,489.494695 C419.703438,495.029445 415.245028,499.529445 409.64207,499.529445 C404.108983,499.529445 399.580702,495.089335 399.580702,489.494695 L399.580702,489.494695 Z"
                            id="Question"
                          ></path>
                        </g>
                      </g>
                    </svg>
                    <h5>
                      <a class="grv-link" href="https://www.capitalone.com/auto-financing/refinance/faq" target="_blank" rel="noopener noreferrer" data-e2e-ref="footerFaqLink">View FAQs</a>
                    </h5>
                    <p>Find answers to frequently asked questions about Auto Refinance.</p>
                  </li>
                </ul>
              </div>
            `
          : ''}
        <refi-common-disclosures></refi-common-disclosures>
        <nav>
          <a class="grv-link" href="https://www.capitalone.com/contact/" target="_blank" rel="noopener noreferrer">Contact Us</a>
          <a class="grv-link" href="https://www.capitalonecareers.com/" target="_blank" rel="noopener noreferrer">Careers</a>
          <a class="grv-link" href="https://www.capitalone.com/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
          <a class="grv-link" href="https://www.capitalone.com/identity-protection/commitment/" target="_blank" rel="noopener noreferrer">Security</a>
          <a class="grv-link" href="https://www.capitalone.com/about/accessibility-commitment/" target="_blank" rel="noopener noreferrer">Accessibility</a>
          <a class="grv-link" href="https://www.capitalone.com/legal/terms-conditions/" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
        </nav>
        <div class="logos">
          <p class="copyright">&copy; Capital One. Capital One is a federally registered trademark. All rights reserved.</p>
          <svg width="104" height="36" viewBox="0 0 384.3 133.7" xmlns="http://www.w3.org/2000/svg" aria-label="Capital One Logo">
            <path
              fill="#D03027"
              d="M252.7 96.7c-15.9 11.3-34.7 23.1-54.9 35.4l-.8.5c-.3.2-.3.6-.1.8.2.3.6.3.8.1l.7-.4c17.1-8.9 36.8-19.6 56.6-30.5.1-.1.2-.1.3-.2-1-1.6-1.9-3.6-2.6-5.7zm104.1-84.3c-25-27.6-185.8-2.8-254.2 12.2l-1.6.3c-.3.1-.5.4-.5.7.1.3.4.5.7.5l1.6-.3c56.7-10.1 174.5-24.1 199.9 1.1 7.7 7.7 5.9 17.6-3.1 29.2 4.8 3.1 8.3 8 9.8 14.1 35-23.3 58.6-45.5 47.4-57.8z"
            ></path>
            <g fill="#004977">
              <path
                d="M262.9 89.4c.3 9.7 6.5 17.6 14.5 17.6 15.5 0 22.1-19 21.7-31.8-.3-9.7-6.6-17.7-14.6-17.7-13.5.2-22.1 19.1-21.6 31.9zm-8.2-.5c-.6-16.6 12.4-33.5 31.2-33.5 12.6 0 20.8 8.4 21.3 21.3.6 17.5-11.5 33.5-31.2 33.5-12.6 0-20.9-8.4-21.3-21.3zM76.2 84.5c-.7.3-1.5.5-2.6.7-1.1.2-3 .6-5.9 1-2 .3-3.3.7-4.1 1.3-.7.6-1.2 1.2-1.3 2-.2.9.1 1.5.9 2.1.8.5 2 .8 3.8.8 1.3 0 2.6-.2 3.9-.6 1.3-.4 2.4-1 3.2-1.7.6-.5 1-1.2 1.4-2 .1-.5.4-1.7.7-3.6zm16.4-10.1c-.1 1.5-.3 2.9-.7 5.2l-2.8 15.6c-.2 1.4.3 2.5 1.6 3.2l-.1.6H74.7l-.1-4c-2.3 1.4-5.1 2.6-7.6 3.3-2.5.6-4.5.9-7.6.9-5.1 0-8.1-.5-10.1-2.3-2.1-1.8-3.1-3-3-5.6.1-1.4.8-3.3 1.9-4.6 1.1-1.3 2.6-2.2 4.2-2.9C54 83 56 82.5 58.8 82c2.8-.5 6.7-.9 11.9-1.3 2.7-.2 4.4-.9 5.2-1.2 1.1-.4 1.5-.9 1.7-1.8.3-1.6-.2-2.5-2.3-2.9-5.7-1.1-16.4.7-22 2.1l3-8.1c7.3-1.1 14.1-1.7 21.3-1.7 11.4-.2 15.1 3.1 15 7.3zM138.6 98.9l5.5-31.1h15.2l-5.5 31.1h-15.2zm6.3-38.3c.5-2.6 4.5-4.6 9-4.6s7.8 2.1 7.4 4.6c-.5 2.6-4.5 4.6-9 4.6-4.6.1-7.8-2-7.4-4.6zM213.2 84.6c-.7.2-1.6.5-2.6.7-1.1.3-3 .6-5.9 1-2 .3-3.3.7-4.1 1.3-.7.5-1.2 1.2-1.3 2-.2.9.2 1.6.9 2.1.8.5 2 .8 3.8.8 1.3 0 2.6-.2 3.9-.6 1.3-.4 2.4-1 3.2-1.7.6-.5 1-1.2 1.4-2 .1-.5.4-1.7.7-3.6zm16.4-10.2c-.1 1.5-.3 2.9-.8 5.2L226 95.2c-.2 1.4.3 2.5 1.5 3.2l-.1.5h-15.9l-.1-4c-2.3 1.4-5.1 2.6-7.6 3.3-2.5.7-4.6.9-7.6.9-5.1 0-8-.5-10.1-2.3-2.1-1.8-3.1-3-3-5.6.1-1.5.8-3.3 1.9-4.6 1.1-1.3 2.6-2.2 4.2-3 1.6-.8 3.6-1.3 6.3-1.8 2.8-.4 6.7-.9 12-1.3 2.7-.2 4.4-.8 5.2-1.1 1.1-.4 1.5-.9 1.7-1.8.3-1.6-.2-2.5-2.3-2.9-5.7-1.1-16.4.7-22 2.1l3-8.1c7.3-1.1 14.1-1.8 21.3-1.8 11.6.1 15.3 3.3 15.2 7.5zM44.2 87.7c-4.9 1-7.7 1.4-12.6 1.4-7.3 0-13-3.6-12.7-10.2.2-4.4 5.4-13.7 18.7-13.7 4.2 0 7.4.7 11.8 3.2l1.9-10.8c-6-2.3-9.9-2.6-15-2.5-17.4.2-34 8.1-36.1 24.1C-1.9 95.5 18 99.6 26.8 99.6c5.1 0 10.4-.2 15.4-.7l2-11.2zM167.6 67.7l1.1-6.3 15.9-3.5-1.7 9.8h7.8l-1.5 6.3h-7.5l-4.6 25s-15.3-.1-15.4 0l4.6-24.9h-5.8l1.2-6.3h5.9zM245 98.9h-15.5l7.8-43.3 15.1.4zM118.5 75.1c-2 0-3.8.6-5.4 1.8-1.6 1.2-2.6 3-3 5.3-.5 2.7-.2 4.7.9 5.9 1.1 1.2 2.6 1.8 4.7 1.8 1.4 0 2.8-.2 3.9-.8 1.3-.7 2.2-1.4 3-2.6.8-1.2 1.4-2.5 1.7-4.1.4-2.5.1-4.3-1.1-5.5-1.2-1.2-2.8-1.8-4.7-1.8zm-27.7 34.5L98.2 68h13.3l-1 5.3c1.2-1.6 3-2.9 5.6-4 2.6-1.1 5.5-1.8 8.6-1.8 3.5 0 5.6.2 8.2 1.5 2.6 1.4 4.4 3.3 5.4 5.9 1 2.5 1.2 5.4.7 8.5-.9 5.1-3.4 9.2-7.5 12.2s-7.7 3.8-13 3.8c-1.9 0-3.4-.2-4.7-.5-1.3-.3-2.3-.7-3-1.2-.7-.5-1.5-1.2-2.5-2.3l-2.5 14.3h-15zM366.6 78.9c-.1-2.5-1.3-4.2-3.9-4.2-6.2 0-12.9 12.2-14 17.1 10.2.1 18.1-6 17.9-12.9zm1.3 19.2l1.2.8c-3 6.2-9.2 11.2-16.5 11.2-5.9 0-10.8-4-11.1-11.4-.5-13.3 12-25.7 22.2-25.7 4.4 0 8.4 1.9 8.6 6.6.4 10.6-14.5 13.8-24.3 14-.3 1.1-.4 2.2-.4 3.9.2 4.4 2.8 8.1 8.3 8.1 4.9 0 9.6-3.8 12-7.5z"
              ></path>
              <path
                d="M307 78.6c1.1-.1 2.8-.3 3.5-.3 1.1 0 2.2.2 2.2 1.3 0 .7-1.2 5.4-1.4 6.5l-2.4 10.4c-1 4.3-2 8.8-2.9 12.3h6.1l3.4-16.6c10.4-10.9 14.5-14.4 16.9-14.4 1.1 0 1.9.6 1.9 1.9.1 1.9-1.1 6.3-1.5 7.5l-3.5 12.1c-.8 2.7-1.4 5.2-1.3 6.9.1 2.7 1.7 3.8 4.1 3.8 4.4 0 7.7-4.6 10.5-9.2l-.8-1.5c-1.1 1.8-3.8 5.8-5.9 5.8-.7 0-1.3-.4-1.3-1.5-.1-1.4.5-3.5.9-4.9l3.9-14.1c1-3.9 1.5-6.4 1.5-7.7-.1-2.6-1.6-3.9-4-3.9-4 0-9.3 2.9-20.6 15.9h-.2l1.4-6.3c.8-3.5 1.5-7.1 2.3-9.6-3.9 1.4-9.5 3-12.9 3.6l.1 2z"
              ></path>
            </g>
            <path
              fill="#004977"
              d="M373.3 67.3c0-3.1 2.5-5.4 5.5-5.4 2.9 0 5.5 2.3 5.5 5.4 0 3.1-2.5 5.4-5.5 5.4-3 .1-5.5-2.2-5.5-5.4zm5.5 4.6c2.4 0 4.4-1.9 4.4-4.5s-1.9-4.5-4.4-4.5c-2.5 0-4.4 1.9-4.4 4.5s1.9 4.5 4.4 4.5zm-1.1-1.4h-1v-6.2h2.4c1.5 0 2.2.5 2.2 1.8 0 1.1-.7 1.6-1.6 1.7l1.8 2.7h-1.1l-1.6-2.7h-1.1v2.7zm1.1-3.5c.8 0 1.5-.1 1.5-1 0-.8-.7-.9-1.4-.9h-1.3V67h1.2z"
            ></path>
          </svg>
        </div>
      </footer>
    `;
  }
}

export { RefiFooter };
